import { useState, useEffect } from 'react';
import { Routes, Route, NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShoppingCart,
  Package,
  ClipboardList,
  BarChart3,
  Edit,
  Trash2,
  Bitcoin,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Globe,
  Download,
  Eye,
  Palette,
  Save,
  Loader2,
  Upload,
  X,
  Image,
  Phone,
  Mail,
  MessageCircle,
  Send,
  Lightbulb,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, API_BASE } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// New Products module
import { ProductsPage } from '@/features/products';

interface Product {
  id: string;
  name: string;
  description?: string;
  code?: string;
  price: number;
  quantity: number;
  category?: string;
  imageUrl?: string;
  isActive: boolean;
  isFeatured: boolean;
}

interface Order {
  id: string;
  orderNumber: string;
  status: string;
  paymentStatus: string;
  paymentMethod?: string;
  total: number;
  subtotal: number;
  deliveryFee: number;
  customerName: string;
  customerEmail?: string;
  customerPhone: string;
  customerAddress?: string;
  customerCity?: string;
  createdAt: string;
  items: { productId: string; name: string; price: number; quantity: number }[];
  store?: { id: string; name: string };
}

interface Store {
  id: string;
  name: string;
  description?: string;
  slug: string;
  logoUrl?: string;
  bannerUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  email?: string;
  phone?: string;
  whatsapp?: string;
  telegram?: string;
  instagram?: string;
  address?: string;
  city?: string;
  country?: string;
  currency: string;
  language: string;
  showPrices: boolean;
  showStock: boolean;
  enableOrdering: boolean;
  template: 'MODERN' | 'MINIMAL' | 'CATALOG' | 'GRID';
  customCss?: string;
  isPublished: boolean;
  publishedAt?: string;
  // Payment settings
  acceptCrypto: boolean;
  acceptCard: boolean;
  acceptCash: boolean;
  cryptoWallets?: { BTC?: string; ETH?: string; USDT?: string };
  minOrderAmount?: number;
  deliveryFee?: number;
  freeDeliveryFrom?: number;
}

// ProductsPage is now imported from @/features/products

// Orders Page
function OrdersPage() {
  const queryClient = useQueryClient();
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  
  const { data, isLoading } = useQuery({
    queryKey: ['orders'],
    queryFn: () => api.get<{ orders: Order[] }>('/orders'),
  });

  const orders = data?.orders || [];

  // Update order mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: string; status?: string; paymentStatus?: string }) =>
      api.patch(`/orders/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setSelectedOrder(null);
    },
  });

  // Status flow: PENDING → CONFIRMED → PROCESSING → SHIPPED → DELIVERED
  const getNextStatus = (current: string) => {
    const flow: Record<string, string> = {
      'PENDING': 'CONFIRMED',
      'CONFIRMED': 'PROCESSING', 
      'PROCESSING': 'SHIPPED',
      'SHIPPED': 'DELIVERED',
    };
    return flow[current] || null;
  };

  const getStatusAction = (status: string) => {
    const actions: Record<string, string> = {
      'PENDING': 'Confirm',
      'CONFIRMED': 'Start Processing',
      'PROCESSING': 'Ship',
      'SHIPPED': 'Mark Delivered',
    };
    return actions[status] || null;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-500/20 text-yellow-400';
      case 'CONFIRMED': return 'bg-blue-500/20 text-blue-400';
      case 'PROCESSING': return 'bg-purple-500/20 text-purple-400';
      case 'SHIPPED': return 'bg-cyan-500/20 text-cyan-400';
      case 'DELIVERED': return 'bg-green-500/20 text-green-400';
      case 'CANCELLED': return 'bg-red-500/20 text-red-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const getPaymentIcon = (status: string) => {
    switch (status) {
      case 'PAID': return <CheckCircle size={16} className="text-green-400" />;
      case 'PENDING': return <Clock size={16} className="text-yellow-400" />;
      case 'FAILED': return <XCircle size={16} className="text-red-400" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg lg:text-xl font-semibold text-slate-900">Orders</h2>
        <span className="text-sm text-slate-500">{orders.length} total</span>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <ClipboardList size={48} className="mx-auto mb-4 opacity-50" />
          <p>No orders yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-gray-200 rounded-xl p-3 lg:p-4 hover:border-gray-300 transition-colors"
            >
              {/* Mobile-first layout */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono text-sm lg:text-base text-slate-900 font-semibold">
                      {order.orderNumber}
                    </span>
                    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium', getStatusColor(order.status))}>
                      {order.status}
                    </span>
                  </div>
                  <p className="font-medium text-slate-900 text-sm truncate">{order.customerName}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-lg lg:text-xl font-bold text-orange-500">
                    €{Number(order.total).toFixed(2)}
                  </p>
                  <p className="text-xs text-slate-400">
                    {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              
              {/* Payment & contact info - collapsible on mobile */}
              <div className="flex items-center gap-2 mb-3 flex-wrap text-xs">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-slate-700 border border-gray-200">
                  {getPaymentIcon(order.paymentStatus)}
                  {order.paymentStatus}
                </span>
                {order.customerPhone && (
                  <a 
                    href={`tel:${order.customerPhone}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                  >
                    <Phone size={12} />
                    Call
                  </a>
                )}
                <span className="text-slate-400 ml-auto">
                  {new Date(order.createdAt).toLocaleDateString()}
                </span>
              </div>
              
              {/* Items preview */}
              <div className="text-xs text-slate-500 mb-3 line-clamp-1">
                {order.items.map((item: any, i: number) => (
                  <span key={i}>
                    {item.name || item.productName} ×{item.quantity}
                    {i < order.items.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
              
              {/* Action buttons - horizontal on mobile */}
              <div className="flex gap-2 pt-3 border-t border-gray-100">
                {getNextStatus(order.status) && order.status !== 'CANCELLED' && (
                  <button
                    onClick={() => updateMutation.mutate({ 
                      id: order.id, 
                      status: getNextStatus(order.status)! 
                    })}
                    disabled={updateMutation.isPending}
                    className="flex-1 px-3 py-2.5 text-sm font-medium rounded-lg bg-amber-500 text-white hover:bg-amber-600 active:bg-amber-700 transition-colors"
                  >
                    {getStatusAction(order.status)}
                  </button>
                )}
                {order.paymentStatus === 'PENDING' && (
                  <button
                    onClick={() => updateMutation.mutate({ 
                      id: order.id, 
                      paymentStatus: 'PAID' 
                    })}
                    disabled={updateMutation.isPending}
                    className="flex-1 px-3 py-2.5 text-sm font-medium rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 active:bg-emerald-700 transition-colors"
                  >
                    Mark Paid
                  </button>
                )}
                <button
                  onClick={() => setSelectedOrder(order)}
                  className="px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-300 text-slate-600 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                >
                  Details
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Order Details Modal - Mobile optimized */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-end lg:items-center justify-center z-50 lg:p-4">
          <motion.div
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="bg-white w-full max-lg:h-[90vh] max-lg:rounded-t-2xl lg:rounded-2xl lg:max-w-lg lg:max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex justify-between items-center px-4 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <div>
                <h3 className="text-lg font-bold text-slate-900">{selectedOrder.orderNumber}</h3>
                <p className="text-sm text-slate-500">
                  {new Date(selectedOrder.createdAt).toLocaleDateString()}
                </p>
              </div>
              <button 
                onClick={() => setSelectedOrder(null)} 
                className="p-2 -mr-2 text-slate-500 hover:text-slate-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {/* Customer */}
              <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                <h4 className="text-sm font-semibold text-slate-900 mb-2">Customer</h4>
                <p className="text-slate-900 font-medium">{selectedOrder.customerName}</p>
                {selectedOrder.customerPhone && (
                  <a 
                    href={`tel:${selectedOrder.customerPhone}`}
                    className="text-slate-600 text-sm flex items-center gap-1.5 mt-1 hover:text-green-600"
                  >
                    <Phone size={14} /> {selectedOrder.customerPhone}
                  </a>
                )}
                {selectedOrder.customerEmail && (
                  <a 
                    href={`mailto:${selectedOrder.customerEmail}`}
                    className="text-slate-600 text-sm flex items-center gap-1.5 mt-1 hover:text-blue-600"
                  >
                    <Mail size={14} /> {selectedOrder.customerEmail}
                  </a>
                )}
                {selectedOrder.customerAddress && (
                  <p className="text-slate-500 text-sm mt-1">
                    📍 {selectedOrder.customerAddress}{selectedOrder.customerCity ? `, ${selectedOrder.customerCity}` : ''}
                  </p>
                )}
              </div>

              {/* Items */}
              <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                <h4 className="text-sm font-semibold text-slate-900 mb-2">Items</h4>
                {selectedOrder.items.map((item: any, i: number) => (
                  <div key={i} className="flex justify-between text-sm py-1.5">
                    <span className="text-slate-700">{item.name || item.productName} ×{item.quantity}</span>
                    <span className="text-slate-900 font-semibold">€{(Number(item.price || item.totalPrice) * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <div className="border-t border-gray-200 mt-2 pt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="text-slate-900">€{Number(selectedOrder.subtotal).toFixed(2)}</span>
                  </div>
                  {selectedOrder.deliveryFee > 0 && (
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-slate-600">Delivery</span>
                      <span className="text-slate-900">€{Number(selectedOrder.deliveryFee).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold mt-2 text-base">
                    <span className="text-slate-900">Total</span>
                    <span className="text-orange-500">€{Number(selectedOrder.total).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Status & Payment */}
              <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                <h4 className="text-sm font-semibold text-slate-900 mb-2">Status</h4>
                <div className="flex gap-2 flex-wrap">
                  <span className={clsx('text-xs px-2.5 py-1 rounded-lg font-medium', getStatusColor(selectedOrder.status))}>
                    {selectedOrder.status}
                  </span>
                  <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-gray-100 text-slate-700 border border-gray-200 font-medium">
                    {getPaymentIcon(selectedOrder.paymentStatus)}
                    {selectedOrder.paymentStatus}
                  </span>
                  {selectedOrder.paymentMethod && (
                    <span className="text-xs px-2.5 py-1 rounded-lg bg-gray-100 text-slate-700 border border-gray-200 font-medium">
                      {selectedOrder.paymentMethod.replace('crypto_', '').toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Actions - Sticky footer */}
            <div className="px-4 py-4 border-t border-gray-200 bg-white safe-area-bottom">
              <div className="flex gap-2">
                {getNextStatus(selectedOrder.status) && selectedOrder.status !== 'CANCELLED' && (
                  <button
                    onClick={() => {
                      updateMutation.mutate({ 
                        id: selectedOrder.id, 
                        status: getNextStatus(selectedOrder.status)! 
                      });
                    }}
                    disabled={updateMutation.isPending}
                    className="flex-1 py-3 px-4 rounded-xl font-semibold text-white bg-amber-500 hover:bg-amber-600 active:bg-amber-700 transition-colors disabled:opacity-50"
                  >
                    {getStatusAction(selectedOrder.status)}
                  </button>
                )}
                {selectedOrder.paymentStatus === 'PENDING' && (
                  <button
                    onClick={() => {
                      updateMutation.mutate({ 
                        id: selectedOrder.id, 
                        paymentStatus: 'PAID' 
                      });
                    }}
                    disabled={updateMutation.isPending}
                    className="flex-1 py-3 px-4 rounded-xl font-semibold text-white bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 transition-colors disabled:opacity-50"
                  >
                    Mark Paid
                  </button>
                )}
                {selectedOrder.status !== 'CANCELLED' && selectedOrder.status !== 'DELIVERED' && (
                  <button
                    onClick={() => {
                      if (confirm('Cancel this order?')) {
                        updateMutation.mutate({ 
                          id: selectedOrder.id, 
                          status: 'CANCELLED' 
                        });
                      }
                    }}
                    disabled={updateMutation.isPending}
                    className="py-3 px-4 rounded-xl font-semibold text-red-600 bg-red-50 hover:bg-red-100 active:bg-red-200 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

// Storefront Page
function StorefrontPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  // Fetch store
  const { data: storeData, isLoading } = useQuery({
    queryKey: ['store'],
    queryFn: () => api.get<{ store: Store | null }>('/storefront/store'),
  });

  // Fetch products count
  const { data: productsData } = useQuery({
    queryKey: ['products'],
    queryFn: () => api.get<{ products: Product[] }>('/products'),
  });

  const store = storeData?.store;
  const activeProducts = productsData?.products?.filter(p => p.isActive) || [];

  // Initial form state
  const getDefaultForm = (): Partial<Store> => ({
    name: '',
    description: '',
    primaryColor: '#f97316',
    secondaryColor: '#1e293b',
    accentColor: '#22c55e',
    currency: 'EUR',
    language: 'en',
    showPrices: true,
    showStock: false,
    enableOrdering: true,
    template: 'MODERN',
    acceptCrypto: false,
    acceptCard: false,
    acceptCash: true,
    cryptoWallets: {},
  });

  const [form, setForm] = useState<Partial<Store>>(getDefaultForm());

  // Update form when store loads
  useEffect(() => {
    if (store) {
      setForm({
        name: store.name,
        description: store.description || '',
        logoUrl: store.logoUrl || '',
        bannerUrl: store.bannerUrl || '',
        primaryColor: store.primaryColor,
        secondaryColor: store.secondaryColor,
        accentColor: store.accentColor,
        email: store.email || '',
        phone: store.phone || '',
        whatsapp: store.whatsapp || '',
        telegram: store.telegram || '',
        instagram: store.instagram || '',
        address: store.address || '',
        city: store.city || '',
        country: store.country || '',
        currency: store.currency,
        language: store.language,
        showPrices: store.showPrices,
        showStock: store.showStock,
        enableOrdering: store.enableOrdering,
        template: store.template,
        acceptCrypto: store.acceptCrypto,
        acceptCard: store.acceptCard,
        acceptCash: store.acceptCash,
        cryptoWallets: store.cryptoWallets || {},
        minOrderAmount: store.minOrderAmount,
        deliveryFee: store.deliveryFee,
        freeDeliveryFrom: store.freeDeliveryFrom,
      });
    }
  }, [store]);

  // Save store mutation
  const saveMutation = useMutation({
    mutationFn: (data: Partial<Store>) => 
      store 
        ? api.patch('/storefront/store', data)
        : api.post('/storefront/store', data),
    onSuccess: () => {
      toast.success('Store saved!');
      queryClient.invalidateQueries({ queryKey: ['store'] });
      setShowForm(false);
    },
    onError: (error: any) => {
      toast.error(error.data?.error || 'Failed to save');
    },
  });

  // Generate storefront mutation
  const generateMutation = useMutation({
    mutationFn: () => api.get<{ html: string; filename: string; productsCount: number }>('/storefront/store/generate'),
    onSuccess: (data) => {
      const blob = new Blob([data.html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded! ${data.productsCount} products included.`);
      queryClient.invalidateQueries({ queryKey: ['store'] });
    },
    onError: (error: any) => {
      toast.error(error.data?.error || 'Failed to generate');
    },
  });

  // Delete store mutation
  const deleteMutation = useMutation({
    mutationFn: () => api.delete('/storefront/store'),
    onSuccess: () => {
      toast.success('Store deleted');
      queryClient.invalidateQueries({ queryKey: ['store'] });
      setForm(getDefaultForm());
    },
    onError: (error: any) => {
      toast.error(error.data?.error || error.data?.details || 'Failed to delete');
    },
  });

  // Image upload states
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  const handleImageUpload = async (file: File, type: 'logo' | 'banner') => {
    const setUploading = type === 'logo' ? setUploadingLogo : setUploadingBanner;
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const { accessToken } = useAuthStore.getState();
      const response = await fetch(`${API_BASE}/products/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Upload failed');
      }

      const data = await response.json();
      // Use functional setState to avoid race condition when uploading both images quickly
      setForm(prevForm => ({
        ...prevForm,
        [type === 'logo' ? 'logoUrl' : 'bannerUrl']: data.url
      }));
      toast.success(`${type === 'logo' ? 'Logo' : 'Banner'} uploaded!`);
    } catch (err: any) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = () => {
    if (!form.name) {
      toast.error('Store name is required');
      return;
    }
    saveMutation.mutate(form);
  };

  const handleDelete = () => {
    if (confirm('Delete this store? This cannot be undone.')) {
      deleteMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  // Show store card if exists and not editing
  if (store && !showForm) {
    return (
      <div className="space-y-4 lg:space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg lg:text-xl font-semibold text-slate-900">My Storefront</h2>
        </div>

        {/* Store Card - Mobile optimized */}
        <div className="card p-4 lg:p-6">
          {/* Mobile layout: stacked, Desktop: side by side */}
          <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6">
            {/* Top row on mobile: Logo + basic info */}
            <div className="flex items-start gap-4 lg:gap-6 flex-1">
              {/* Logo/Icon */}
              <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-xl flex items-center justify-center text-2xl lg:text-3xl font-bold shrink-0 bg-white border-2 border-gray-200 overflow-hidden">
                {store.logoUrl ? (
                  <img src={store.logoUrl} alt="" className="w-full h-full object-contain p-2" />
                ) : (
                  <span className="text-[#4A7C2E]">{store.name.charAt(0).toUpperCase()}</span>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg lg:text-xl font-semibold text-slate-900 truncate">{store.name}</h3>
                {store.description && (
                  <p className="text-slate-500 text-sm mt-1 line-clamp-2">{store.description}</p>
                )}
                
                {/* Badges - horizontal scroll on mobile */}
                <div className="flex flex-wrap gap-2 lg:gap-4 mt-3 text-xs lg:text-sm">
                  <span className="text-slate-600 flex items-center gap-1">
                    <Package size={14} /> {activeProducts.length}
                  </span>
                  {store.whatsapp && (
                    <span className="text-slate-600 flex items-center gap-1"><MessageCircle size={14} /></span>
                  )}
                  {store.telegram && (
                    <span className="text-slate-600 flex items-center gap-1"><Send size={14} /></span>
                  )}
                  <span className="text-slate-600 flex items-center gap-1">
                    {store.currency}
                  </span>
                  {store.acceptCrypto && (
                    <span className="text-green-600 flex items-center gap-1"><Bitcoin size={14} /></span>
                  )}
                </div>

                {/* Status */}
                <div className="flex items-center gap-2 mt-3">
                  {store.isPublished ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                      <CheckCircle size={12} /> Published
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                      <Globe size={12} /> Draft
                    </span>
                  )}
                  {store.isPublished && store.publishedAt && (
                    <span className="text-xs text-slate-500 hidden sm:inline">
                      {new Date(store.publishedAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Actions - Full width on mobile, column on desktop */}
            <div className="flex flex-wrap lg:flex-col gap-2 pt-3 lg:pt-0 border-t lg:border-t-0 border-gray-100">
              <button
                onClick={() => generateMutation.mutate()}
                disabled={generateMutation.isPending}
                className="flex-1 lg:flex-none btn-primary bg-green-600 hover:bg-green-500 active:bg-green-700 whitespace-nowrap min-h-[44px] justify-center"
              >
                {generateMutation.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Download size={16} />
                )}
                <span className="sm:inline">Download</span>
              </button>
              
              <button
                onClick={async () => {
                  try {
                    const data = await api.get<{ html: string }>('/storefront/store/generate');
                    const blob = new Blob([data.html], { type: 'text/html' });
                    const url = URL.createObjectURL(blob);
                    window.open(url, '_blank');
                  } catch {
                    toast.error('Failed to generate preview');
                  }
                }}
                className="flex-1 lg:flex-none btn-secondary whitespace-nowrap min-h-[44px] justify-center"
              >
                <Eye size={16} />
                <span>Preview</span>
              </button>

              <button
                onClick={() => setShowForm(true)}
                className="btn-ghost whitespace-nowrap min-h-[44px] justify-center"
              >
                <Edit size={16} />
                <span className="sm:inline">Edit</span>
              </button>

              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="btn-ghost text-red-500 hover:bg-red-50 active:bg-red-100 whitespace-nowrap min-h-[44px] justify-center"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show form
  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg lg:text-xl font-semibold text-slate-900">
            {store ? 'Edit Store' : 'Create Store'}
          </h2>
          <p className="text-sm text-slate-500 hidden sm:block">Configure your storefront settings</p>
        </div>
        {store && (
          <button onClick={() => setShowForm(false)} className="btn-ghost shrink-0">
            ← Back
          </button>
        )}
      </div>

      {/* Form - Single column on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Basic Info */}
        <div className="card space-y-4 p-4 lg:p-6">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm lg:text-base">
            <Globe size={20} className="text-orange-600" />
            Basic Information
          </h3>
          
          <div>
            <label className="label">Store Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input"
              placeholder="My Awesome Store"
            />
          </div>

          <div>
            <label className="label">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="input min-h-[80px]"
              placeholder="Welcome to our store..."
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
            <div>
              <label className="label">Currency</label>
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className="input"
              >
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
                <option value="THB">THB (฿)</option>
                <option value="RUB">RUB (₽)</option>
              </select>
            </div>
            <div>
              <label className="label">Template</label>
              <select
                value={form.template}
                onChange={(e) => setForm({ ...form, template: e.target.value as Store['template'] })}
                className="input"
              >
                <option value="MODERN">Modern (Dark)</option>
                <option value="MINIMAL">Minimal</option>
                <option value="GRID">Grid</option>
                <option value="CATALOG">Catalog</option>
              </select>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="card space-y-4 p-4 lg:p-6">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm lg:text-base">
            <Phone size={20} className="text-green-600" />
            Contact Information
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
            <div>
              <label className="label">WhatsApp</label>
              <input
                type="text"
                value={form.whatsapp}
                onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                className="input"
                placeholder="+66..."
              />
            </div>
            <div>
              <label className="label">Telegram</label>
              <input
                type="text"
                value={form.telegram}
                onChange={(e) => setForm({ ...form, telegram: e.target.value })}
                className="input"
                placeholder="@username"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="label">Instagram</label>
              <input
                type="text"
                value={form.instagram}
                onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                className="input"
                placeholder="@brand"
              />
            </div>
          </div>
        </div>

        {/* Branding */}
        <div className="card space-y-4 p-4 lg:p-6">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm lg:text-base">
            <Palette size={20} className="text-purple-600" />
            Branding
          </h3>

          {/* Logo Upload */}
          <div>
            <label className="label">Logo</label>
            <div className="flex items-center gap-3 lg:gap-4">
              {form.logoUrl ? (
                <div className="relative">
                  <img src={form.logoUrl} alt="Logo" className="w-16 h-16 lg:w-20 lg:h-20 object-cover rounded-lg border border-gray-300" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, logoUrl: '' })}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-400 active:bg-red-600"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="w-16 h-16 lg:w-20 lg:h-20 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                  <Image size={20} className="text-gray-400" />
                </div>
              )}
              <label className="cursor-pointer flex-1 sm:flex-none">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file, 'logo');
                  }}
                  disabled={uploadingLogo}
                />
                <span className="btn-secondary flex items-center justify-center gap-2 w-full sm:w-auto min-h-[44px]">
                  {uploadingLogo ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                  {uploadingLogo ? 'Uploading...' : 'Upload'}
                </span>
              </label>
            </div>
          </div>

          {/* Banner Upload */}
          <div>
            <label className="label">Banner</label>
            <div className="space-y-2">
              {form.bannerUrl ? (
                <div className="relative">
                  <img src={form.bannerUrl} alt="Banner" className="w-full h-24 lg:h-32 object-cover rounded-lg border border-gray-300" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, bannerUrl: '' })}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-400 active:bg-red-600"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="w-full h-24 lg:h-32 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                  <Image size={32} className="text-gray-400" />
                </div>
              )}
              <label className="cursor-pointer inline-block">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file, 'banner');
                  }}
                  disabled={uploadingBanner}
                />
                <span className="btn-secondary flex items-center gap-2 min-h-[44px]">
                  {uploadingBanner ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                  {uploadingBanner ? 'Uploading...' : 'Upload Banner'}
                </span>
              </label>
            </div>
          </div>

          <p className="text-xs text-slate-600 flex items-start gap-1">
            <Lightbulb size={14} className="text-yellow-600 mt-0.5 shrink-0" /> 
            <span>Re-download HTML after changing images</span>
          </p>

          <div className="grid grid-cols-3 gap-2 lg:gap-4">
            <div>
              <label className="label text-xs">Primary</label>
              <input
                type="color"
                value={form.primaryColor}
                onChange={(e) => setForm({ ...form, primaryColor: e.target.value })}
                className="w-full h-11 rounded cursor-pointer border border-gray-300"
              />
            </div>
            <div>
              <label className="label text-xs">Secondary</label>
              <input
                type="color"
                value={form.secondaryColor}
                onChange={(e) => setForm({ ...form, secondaryColor: e.target.value })}
                className="w-full h-11 rounded cursor-pointer border border-gray-300"
              />
            </div>
            <div>
              <label className="label text-xs">Accent</label>
              <input
                type="color"
                value={form.accentColor}
                onChange={(e) => setForm({ ...form, accentColor: e.target.value })}
                className="w-full h-11 rounded cursor-pointer border border-gray-300"
              />
            </div>
          </div>
        </div>

        {/* Display & Payment */}
        <div className="card space-y-4 p-4 lg:p-6">
          <h3 className="font-semibold text-slate-900 text-sm lg:text-base">Settings</h3>

          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer py-1">
              <input
                type="checkbox"
                checked={form.showPrices}
                onChange={(e) => setForm({ ...form, showPrices: e.target.checked })}
                className="w-5 h-5 rounded"
              />
              <span className="text-slate-700 text-sm lg:text-base">Show prices</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer py-1">
              <input
                type="checkbox"
                checked={form.enableOrdering}
                onChange={(e) => setForm({ ...form, enableOrdering: e.target.checked })}
                className="w-5 h-5 rounded"
              />
              <span className="text-slate-700 text-sm lg:text-base">Enable cart & checkout</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer py-1">
              <input
                type="checkbox"
                checked={form.acceptCash}
                onChange={(e) => setForm({ ...form, acceptCash: e.target.checked })}
                className="w-5 h-5 rounded"
              />
              <span className="text-slate-700 text-sm lg:text-base flex items-center gap-1">
                <DollarSign size={14} /> Cash on delivery
              </span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer py-1">
              <input
                type="checkbox"
                checked={form.acceptCrypto}
                onChange={(e) => setForm({ ...form, acceptCrypto: e.target.checked })}
                className="w-5 h-5 rounded"
              />
              <span className="text-slate-700 text-sm lg:text-base flex items-center gap-1">
                <Bitcoin size={14} /> Accept crypto
              </span>
            </label>
          </div>

          {form.acceptCrypto && (
            <div className="space-y-3 pt-3 border-t border-gray-200">
              <div>
                <label className="label text-xs">Bitcoin (BTC)</label>
                <input
                  type="text"
                  value={form.cryptoWallets?.BTC || ''}
                  onChange={(e) => setForm({ ...form, cryptoWallets: { ...form.cryptoWallets, BTC: e.target.value } })}
                  className="input font-mono text-xs lg:text-sm"
                  placeholder="bc1q..."
                />
              </div>
              <div>
                <label className="label text-xs">Ethereum (ETH)</label>
                <input
                  type="text"
                  value={form.cryptoWallets?.ETH || ''}
                  onChange={(e) => setForm({ ...form, cryptoWallets: { ...form.cryptoWallets, ETH: e.target.value } })}
                  className="input font-mono text-xs lg:text-sm"
                  placeholder="0x..."
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-2 lg:gap-3 pt-3 border-t border-gray-200">
            <div>
              <label className="label text-xs">Min Order</label>
              <input
                type="number"
                value={form.minOrderAmount || ''}
                onChange={(e) => setForm({ ...form, minOrderAmount: Number(e.target.value) || undefined })}
                className="input"
                placeholder="0"
              />
            </div>
            <div>
              <label className="label text-xs">Delivery</label>
              <input
                type="number"
                value={form.deliveryFee || ''}
                onChange={(e) => setForm({ ...form, deliveryFee: Number(e.target.value) || undefined })}
                className="input"
                placeholder="0"
              />
            </div>
            <div>
              <label className="label text-xs">Free From</label>
              <input
                type="number"
                value={form.freeDeliveryFrom || ''}
                onChange={(e) => setForm({ ...form, freeDeliveryFrom: Number(e.target.value) || undefined })}
                className="input"
                placeholder="100"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save Button - Sticky on mobile */}
      <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-4 lg:pt-0">
        {store && (
          <button onClick={() => setShowForm(false)} className="btn-ghost min-h-[44px]">
            Cancel
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={saveMutation.isPending}
          className="btn-primary min-h-[44px]"
        >
          {saveMutation.isPending ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Save size={16} />
          )}
          {store ? 'Save Changes' : 'Create Store'}
        </button>
      </div>
    </div>
  );
}

// Analytics Page
function AnalyticsPage() {
  const [period, setPeriod] = useState<'7d' | '30d'>('30d');
  
  const { data, isLoading } = useQuery({
    queryKey: ['shop-analytics', period],
    queryFn: () => api.get<{ analytics: any }>(`/analytics?period=${period}`),
  });

  const analytics = data?.analytics;
  const charts = analytics?.charts;

  // Format date for chart display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="text-sm font-medium text-slate-900">{formatDate(label)}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.name === 'Revenue' ? `€${entry.value.toFixed(2)}` : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Period Selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900">Analytics</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setPeriod('7d')}
            className={clsx(
              'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
              period === '7d'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-slate-600 hover:bg-gray-200'
            )}
          >
            7 Days
          </button>
          <button
            onClick={() => setPeriod('30d')}
            className={clsx(
              'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
              period === '30d'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-slate-600 hover:bg-gray-200'
            )}
          >
            30 Days
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-orange-100">
              <Package size={24} className="text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {analytics?.products?.total || 0}
              </p>
              <p className="text-sm text-slate-600 font-medium">Total Products</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-blue-100">
              <ClipboardList size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {analytics?.orders?.total || 0}
              </p>
              <p className="text-sm text-slate-600 font-medium">Total Orders</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-green-100">
              <DollarSign size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                €{(analytics?.revenue?.total || 0).toFixed(2)}
              </p>
              <p className="text-sm text-slate-600 font-medium">Total Revenue</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-yellow-100">
              <Clock size={24} className="text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {analytics?.orders?.pending || 0}
              </p>
              <p className="text-sm text-slate-600 font-medium">Pending Orders</p>
            </div>
          </div>
        </div>
      </div>

      {/* Views Chart */}
      <div className="card">
        <h3 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
          <Eye size={20} className="text-purple-600" />
          Store Views
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={charts?.viewsByDay || []}>
              <defs>
                <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fontSize: 12, fill: '#64748b' }}
                axisLine={{ stroke: '#e2e8f0' }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: '#64748b' }}
                axisLine={{ stroke: '#e2e8f0' }}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="count"
                name="Views"
                stroke="#8b5cf6"
                strokeWidth={2}
                fill="url(#viewsGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Orders & Revenue Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Orders Chart */}
        <div className="card">
          <h3 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
            <ClipboardList size={20} className="text-blue-600" />
            Orders
          </h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts?.ordersByDay || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="count"
                  name="Orders"
                  fill="#3b82f6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue Chart */}
        <div className="card">
          <h3 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
            <DollarSign size={20} className="text-green-600" />
            Revenue
          </h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={charts?.revenueByDay || []}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickFormatter={(value) => `€${value}`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="amount"
                  name="Revenue"
                  stroke="#22c55e"
                  strokeWidth={2}
                  fill="url(#revenueGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top Products */}
      {analytics?.topProducts?.length > 0 && (
        <div className="card">
          <h3 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
            <TrendingUp size={20} className="text-orange-600" />
            Top Selling Products
          </h3>
          <div className="space-y-3">
            {analytics.topProducts.map((product: any, i: number) => (
              <div
                key={product.productId}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-sm font-bold text-orange-600">
                    #{i + 1}
                  </span>
                  <span className="text-slate-900 font-medium">{product.name}</span>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-green-600">
                    €{product.revenue?.toFixed(2)}
                  </p>
                  <p className="text-xs text-slate-400">{product.quantity} sold</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!analytics?.topProducts || analytics.topProducts.length === 0) &&
        analytics?.orders?.total === 0 && (
          <div className="card text-center py-12">
            <BarChart3 size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No data yet</h3>
            <p className="text-slate-500">
              Analytics will appear here once you start receiving orders and views.
            </p>
          </div>
        )}
    </div>
  );
}

// Main Component
export default function ShopFather() {
  // Fetch user's store to pass storeId to ProductsPage
  const { data: storeData } = useQuery({
    queryKey: ['shop-store'],
    queryFn: () => api.get<{ store: Store | null }>('/storefront/store'),
  });
  const storeId = storeData?.store?.id;

  return (
    <div className="max-w-6xl mx-auto space-y-4 lg:space-y-6">
      {/* Header - Compact on mobile */}
      <div className="flex items-center gap-3 lg:gap-4">
        <div className="p-2.5 lg:p-3 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600">
          <ShoppingCart size={20} className="lg:w-6 lg:h-6 text-white" />
        </div>
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-900">ShopFather</h1>
          <p className="text-sm lg:text-base text-slate-600 hidden sm:block">Privacy-first e-commerce</p>
        </div>
      </div>

      {/* Navigation - Horizontal scroll on mobile */}
      <div className="flex gap-2 overflow-x-auto pb-4 border-b border-gray-200 no-scrollbar -mx-4 px-4 lg:mx-0 lg:px-0">
        <NavLink
          to="/shop"
          end
          className={({ isActive }) =>
            clsx(
              'btn shrink-0 py-2.5 px-4',
              isActive ? 'bg-orange-500 text-white' : 'btn-ghost'
            )
          }
        >
          <Globe size={18} />
          <span>Store</span>
        </NavLink>
        <NavLink
          to="/shop/products"
          className={({ isActive }) =>
            clsx(
              'btn shrink-0 py-2.5 px-4',
              isActive ? 'bg-orange-500 text-white' : 'btn-ghost'
            )
          }
        >
          <Package size={18} />
          <span>Products</span>
        </NavLink>
        <NavLink
          to="/shop/orders"
          className={({ isActive }) =>
            clsx(
              'btn shrink-0 py-2.5 px-4',
              isActive ? 'bg-orange-500 text-white' : 'btn-ghost'
            )
          }
        >
          <ClipboardList size={18} />
          <span>Orders</span>
        </NavLink>
        <NavLink
          to="/shop/analytics"
          className={({ isActive }) =>
            clsx(
              'btn shrink-0 py-2.5 px-4',
              isActive ? 'bg-orange-500 text-white' : 'btn-ghost'
            )
          }
        >
          <BarChart3 size={18} />
          <span>Stats</span>
        </NavLink>
      </div>

      {/* Content - Less padding on mobile */}
      <div className="card p-3 lg:p-6">
        <Routes>
          <Route index element={<StorefrontPage />} />
          <Route path="products" element={<ProductsPage defaultStoreId={storeId} />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
        </Routes>
      </div>
    </div>
  );
}

